import React, { useRef, useState, useEffect } from 'react';
import { toJpeg } from 'html-to-image';
import { 
  Download, 
  CheckCircle2, 
  Share2, 
  Copy, 
  X, 
  Check, 
  Truck, 
  ShoppingBag, 
  CreditCard, 
  Sparkles,
  ShieldCheck,
  Calendar,
  Hash,
  QrCode
} from 'lucide-react';
import { OrderRecord, DistributorConfig } from '../types';
import { GIFFARINE_OFFICIAL_LOGO } from '../assets/logo';
import { formatThaiDateTime } from '../lib/dateUtils';
import { generatePromptPayDataUrl } from '../lib/promptpayQr';

const maskPhone = (phone: string): string => {
  if (!phone) return '-';
  const cleanPhone = phone.trim();
  if (cleanPhone.length <= 6) return cleanPhone;
  return cleanPhone.slice(0, 6) + '*'.repeat(Math.max(0, cleanPhone.length - 6));
};

const maskAddress = (address: string): string => {
  if (!address) return '-';
  const cleanAddress = address.trim();
  if (cleanAddress.length <= 6) return cleanAddress;
  return cleanAddress.slice(0, 6) + '*'.repeat(Math.min(30, Math.max(0, cleanAddress.length - 6)));
};

interface SalesSlipModalProps {
  order: OrderRecord | null;
  distributor: DistributorConfig;
  isOpen: boolean;
  onClose: () => void;
  qrDataUrl?: string;
}

export const SalesSlipModal: React.FC<SalesSlipModalProps> = ({
  order,
  distributor,
  isOpen,
  onClose,
  qrDataUrl,
}) => {
  const receiptRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hasAutoDownloaded, setHasAutoDownloaded] = useState(false);
  const [slipQrUrl, setSlipQrUrl] = useState<string>(qrDataUrl || '');

  // Generate PromptPay QR if not provided
  useEffect(() => {
    if (qrDataUrl) {
      setSlipQrUrl(qrDataUrl);
      return;
    }
    if (!order) return;

    let isMounted = true;
    generatePromptPayDataUrl({
      promptPayId: '0939678649',
      billerId: '010753700088205',
      merchantCode: 'QV353070BI06881536X',
      merchantName: 'GIFFFORYOU.COM',
      amount: order.totalAmount,
      ref1: 'QV353070BI06881536X',
      ref2: order.orderId,
      isDynamic: true
    }).then((url) => {
      if (isMounted) setSlipQrUrl(url);
    }).catch((err) => {
      console.error('Failed generating slip QR:', err);
    });

    return () => {
      isMounted = false;
    };
  }, [order, qrDataUrl]);

  // Auto-download slip PNG when modal opens
  useEffect(() => {
    if (isOpen && order && !hasAutoDownloaded) {
      const timer = setTimeout(() => {
        handleDownloadSlip(true);
        setHasAutoDownloaded(true);
      }, 700);
      return () => clearTimeout(timer);
    }
    if (!isOpen) {
      setHasAutoDownloaded(false);
    }
  }, [isOpen, order]);

  if (!isOpen || !order) return null;

  const handleDownloadSlip = async (isAuto = false) => {
    if (!receiptRef.current) return;
    try {
      setDownloading(true);
      const dataUrl = await toJpeg(receiptRef.current, {
        cacheBust: true,
        quality: 0.95, // High-quality JPG HD
        pixelRatio: 3, // Ultra-sharp high-definition render for mobile & print
        backgroundColor: '#ffffff',
      });

      const link = document.createElement('a');
      link.download = `Slip-${order.orderId}.jpg`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Failed to capture receipt slip:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleCopySummary = () => {
    const text = `✓ บันทึกคำสั่งซื้อสำเร็จ - GIFFARINE ONLINE
รหัสคำสั่งซื้อ: ${order.orderId}
MID: 108-037654
เลขที่ลูกค้า: ${order.customerId || 'ลูกค้าทั่วไป'}
วันที่-เวลา: ${formatThaiDateTime(order.timestamp)}
ลูกค้า: ${order.customerName} (${maskPhone(order.phone)})
ที่อยู่รับสินค้า: ${maskAddress(order.address)}

รายการสินค้า:
${order.items.map(i => `• ${i.productName} (รหัส: ${i.productCode}) x${i.quantity} = ฿${i.subtotal.toLocaleString()}`).join('\n')}

ยอดรวมสินค้า: ฿${order.subtotal.toLocaleString()}
ค่าจัดส่ง: ${order.deliveryFee > 0 ? `฿${order.deliveryFee}` : 'ฟรี'}
ยอดชำระสุทธิ: ฿${order.totalAmount.toLocaleString()}
วิธีชำระเงิน: ${order.paymentMethod === 'BANK_TRANSFER' ? 'โอนเงินผ่านธนาคาร' : 'COD'}
สถานะ: ${order.paymentStatus === 'Paid' ? 'ชำระแล้ว' : 'รอชำระ'}`;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(() => {});
      }
    } catch (e) {
      console.warn('Clipboard write error:', e);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto">
      <div className="relative w-full max-w-[446px] my-auto bg-white rounded-3xl shadow-2xl overflow-hidden border border-blue-100 animate-in fade-in zoom-in-95 duration-200">
        
        {/* Modal Top Action Bar */}
        <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-blue-50/80 via-white to-indigo-50/40 border-b border-blue-100">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-bold text-slate-800 tracking-wide">สลิปยืนยันคำสั่งซื้อ (Sales Slip)</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 rounded-full hover:bg-blue-100/60 transition-colors cursor-pointer"
            title="ปิดหน้าต่าง"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable Receipt Area */}
        <div className="p-3 sm:p-4 max-h-[75vh] overflow-y-auto bg-slate-100/80 flex justify-center">
          
          {/* Printable Thermal/Digital Sales Slip Layout Container - ปรับขยายความกว้างซ้ายขวาเพิ่มข้างละ 8px (รวมเป็น 396px) และไม่มีการจำกัดความสูงเพื่อให้รองรับออเดอร์ขนาดใหญ่หรือโปรโมชั่นยาวได้เต็มที่ */}
          <div
            ref={receiptRef}
            id="giffarine-sales-receipt"
            className="w-full max-w-[396px] h-auto min-h-fit bg-white p-5 rounded-2xl shadow-sm border border-slate-200/90 text-slate-800 text-[13px] relative flex flex-col overflow-hidden"
            style={{ fontFamily: "'Prompt', -apple-system, BlinkMacSystemFont, sans-serif" }}
          >
            {/* Elegant repeating 45-degree watermark overlay */}
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden z-0 flex flex-wrap justify-around items-center opacity-[0.035] p-4 gap-y-24 gap-x-12">
              {Array.from({ length: 15 }).map((_, i) => (
                <div
                  key={i}
                  className="text-[#0E2C58] font-bold text-[11px] tracking-widest uppercase transform -rotate-45 p-4 border border-[#0E2C58]/25 rounded-md"
                  style={{ minWidth: '130px', textAlign: 'center' }}
                >
                  GIFFFORYOU.COM
                </div>
              ))}
            </div>

            {/* Header / Brand Identity */}
            <div className="text-center pb-4 border-b border-dashed border-slate-300 relative z-10">
              <div className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-[#0E2C58] mb-2 shadow-xs">
                <img
                  src={GIFFARINE_OFFICIAL_LOGO}
                  alt="GIFFARINE"
                  className="h-6 sm:h-7 w-auto object-contain"
                />
              </div>
              <h1 className="text-[14px] font-bold text-slate-800 mt-1">
                GIFFARINE ONLINE STORE
              </h1>
              <p className="text-[11px] text-slate-500 font-medium">
                ร้านผู้จัดจำหน่ายกิฟฟารีนอิสระอย่างเป็นทางการ
              </p>
              <div className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold border border-emerald-200 shadow-2xs whitespace-nowrap">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="whitespace-nowrap">บันทึกคำสั่งซื้อสำเร็จ</span>
              </div>
            </div>

            {/* Meta Details (Order ID, Date, Payment Info) with Strict Grid Alignment */}
            <div className="py-3 border-b border-dashed border-slate-300 space-y-2 relative z-10">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 whitespace-nowrap flex items-center gap-1">
                  <Hash className="w-3 h-3 text-slate-400" />
                  เลขที่คำสั่งซื้อ:
                </span>
                <span className="font-bold text-slate-900 font-mono text-[12.5px] bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
                  {order.orderId}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 whitespace-nowrap flex items-center gap-1">
                  <Hash className="w-3 h-3 text-slate-400" />
                  รหัสสมาชิก (MID):
                </span>
                <span className="font-bold text-[#0E2C58] font-mono text-[12px] bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
                  108-037654
                </span>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 whitespace-nowrap flex items-center gap-1">
                  <Hash className="w-3 h-3 text-slate-400" />
                  เลขที่ลูกค้า:
                </span>
                <span className="font-bold text-[#2A59FE] font-mono text-[12px] bg-blue-50/70 px-2 py-0.5 rounded-md border border-blue-100/60">
                  {order.customerId || 'ลูกค้าทั่วไป'}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 whitespace-nowrap flex items-center gap-1">
                  <Calendar className="w-3 h-3 text-slate-400" />
                  วันที่-เวลา:
                </span>
                <span className="font-medium text-slate-700 font-mono text-[11.5px]">
                  {formatThaiDateTime(order.timestamp)}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 whitespace-nowrap flex items-center gap-1">
                  <CreditCard className="w-3 h-3 text-slate-400" />
                  ช่องทางชำระเงิน:
                </span>
                <span className="font-bold text-slate-800 whitespace-nowrap">
                  {order.paymentMethod === 'BANK_TRANSFER' ? 'โอนเงินผ่านธนาคาร' : 'COD'}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 whitespace-nowrap flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-slate-400" />
                  สถานะชำระ:
                </span>
                <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${
                  order.paymentStatus === 'Paid'
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                    : 'bg-blue-50 text-blue-700 border border-blue-200'
                }`}>
                  {order.paymentStatus === 'Paid' ? 'ชำระแล้ว' : 'รอชำระ'}
                </span>
              </div>
            </div>

            {/* Items Table Section */}
            <div className="py-3 border-b border-dashed border-slate-300 relative z-10">
              <div className="flex items-center justify-between text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 pb-1 border-b border-slate-100">
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <ShoppingBag className="w-3 h-3" />
                  รายการสินค้า
                </span>
                <span className="whitespace-nowrap">จำนวน / รวม</span>
              </div>

              <div className="space-y-2.5">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-start text-xs gap-2">
                    <div className="flex-1 min-w-0 pr-1">
                      <p className="font-semibold text-slate-900 text-[12px] leading-snug">
                        {item.productName}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-slate-500 font-mono bg-slate-100 px-1.5 py-0.2 rounded">
                          รหัส: {item.productCode}
                        </span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="inline-block text-slate-500 text-[11px] font-medium mr-1.5 whitespace-nowrap">
                        x{item.quantity}
                      </span>
                      <span className="font-bold text-slate-900 font-mono text-[12.5px] whitespace-nowrap">
                        ฿{item.subtotal.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Financial Totals */}
            <div className="pt-3 pb-3 border-b border-dashed border-slate-300 text-xs space-y-1.5 relative z-10">
              <div className="flex justify-between items-center text-slate-600">
                <span className="whitespace-nowrap">ยอดรวมสินค้า:</span>
                <span className="font-mono font-medium">฿{order.subtotal.toLocaleString()}</span>
              </div>

              <div className="flex justify-between items-center text-slate-600">
                <span className="whitespace-nowrap">ค่าจัดส่ง:</span>
                <span className="font-mono font-medium">
                  {order.deliveryFee > 0 ? `฿${order.deliveryFee.toLocaleString()}` : 'จัดส่งฟรี'}
                </span>
              </div>

              {/* Grand Total Row with High Visual Contrast */}
              <div className="pt-2.5 mt-2 border-t-2 border-slate-900 flex justify-between items-baseline">
                <span className="text-sm font-extrabold text-slate-900 whitespace-nowrap">
                  ยอดชำระสุทธิ:
                </span>
                <span className="text-2xl font-black text-[#0E2C58] font-mono tracking-tight whitespace-nowrap">
                  ฿{order.totalAmount.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Customer & Shipping Information (จัดแสดงตำแหน่งด้านล่างยอดชำระสุทธิตามข้อกำหนดสถาปัตยกรรม) */}
            <div className="py-3 border-b border-dashed border-slate-300 text-xs relative z-10">
              <div className="flex items-center gap-1.5 text-[#0E2C58] font-bold mb-1.5 whitespace-nowrap">
                <Truck className="w-3.5 h-3.5 text-[#2A59FE] shrink-0" />
                <span className="whitespace-nowrap">ข้อมูลผู้รับสินค้า & จัดส่ง</span>
              </div>
              <div className="bg-slate-50/80 p-2.5 rounded-xl border border-slate-200 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-900 text-[12.5px]">{order.customerName}</span>
                  <span className="font-mono text-slate-700 text-[11.5px] whitespace-nowrap bg-white px-2 py-0.5 rounded border border-slate-200">
                    โทร: {maskPhone(order.phone)}
                  </span>
                </div>
                <p className="text-slate-600 text-[11px] leading-relaxed pt-1 border-t border-slate-200/60">
                  <span className="text-slate-400 mr-1">ที่อยู่:</span>
                  {maskAddress(order.address)}
                </p>
                {order.notes && (
                  <p className="text-[10.5px] text-slate-500 italic pt-0.5">
                    หมายเหตุ: {order.notes}
                  </p>
                )}
              </div>
            </div>

            {/* QR Code Section - แสดงด้านล่างสลิป */}
            <div className="mt-3.5 pt-3 border-t-2 border-dashed border-slate-300 text-center relative z-10">
              <div className="inline-flex items-center justify-center gap-1 px-2.5 py-0.5 rounded-full bg-[#002E6E] text-white text-[10px] font-bold mb-1 shadow-2xs">
                <span>THAI QR PAYMENT • พร้อมเพย์</span>
              </div>
              <p className="text-[11px] text-slate-600 font-medium mb-2">
                สแกน QR Code ด้านล่างนี้เพื่อชำระเงิน
              </p>

              {/* QR Code Box */}
              <div className="bg-white p-2 rounded-xl border border-slate-300 shadow-2xs inline-block mx-auto">
                {slipQrUrl ? (
                  <img
                    src={slipQrUrl}
                    alt="PromptPay QR Code"
                    className="w-40 h-40 object-contain mx-auto"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <div className="w-40 h-40 flex items-center justify-center bg-slate-50 text-slate-400 text-xs">
                    กำลังสร้าง QR Code...
                  </div>
                )}
              </div>

              {/* Ref Tags & Payment Info */}
              <div className="mt-2 space-y-0.5 text-[10.5px] font-mono text-slate-700">
                <div><span className="text-slate-500 font-sans">รหัสสมาชิก (MID):</span> <strong className="font-bold text-[#0E2C58]">108-037654</strong></div>
                <div><span className="text-slate-500 font-sans">Biller ID:</span> <strong className="font-bold text-slate-800">010753700088205</strong></div>
                <div><span className="text-slate-500 font-sans">รหัสผู้ค้า (Ref 1):</span> <strong className="font-bold text-blue-900">QV353070BI06881536X</strong></div>
                <div><span className="text-slate-500 font-sans">รหัสคำสั่งซื้อ (Ref 2):</span> <strong className="font-bold text-slate-800">{order.orderId}</strong></div>
                <div><span className="text-slate-500 font-sans">ยอดชำระ:</span> <strong className="font-bold text-[#0E2C58]">฿{order.totalAmount.toLocaleString()}</strong></div>
              </div>

              <div className="mt-2 py-1 px-2 rounded-lg bg-blue-50/90 border border-blue-200/80 text-[10px] text-blue-900 text-center font-medium">
                ⚡ เมื่อชำระเงินแล้ว แอดมินจะตรวจสอบยอดเงินและแนบสลิปเข้าระบบหลังบ้านให้อัตโนมัติ
              </div>
            </div>

            {/* Receipt Footer & Contact Information */}
            <div className="mt-4 pt-3 border-t border-dashed border-slate-300 text-center text-[11px] text-slate-500 space-y-1 relative z-10">
              <p className="font-medium text-slate-700">ขอบพระคุณที่ไว้วางใจผลิตภัณฑ์กิฟฟารีน</p>
              <p className="text-[10.5px]">
                ติดต่อสอบถาม / ติดตามสถานะจัดส่ง LINE:{' '}
                <span className="font-bold text-[#0E2C58]">@{distributor.lineId.replace('@', '')}</span>
              </p>
              <div className="pt-1.5 flex items-center justify-center gap-1 text-[9px] text-slate-400 font-mono">
                <ShieldCheck className="w-3 h-3 text-emerald-600" />
                <span>SECURITY VERIFIED TRANSACTION SLIP</span>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Bottom Action Buttons */}
        <div className="p-4 bg-white border-t border-slate-100 space-y-2.5">
          <button
            onClick={() => handleDownloadSlip(false)}
            disabled={downloading}
            className="w-full py-3 px-4 bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] hover:opacity-95 text-white rounded-2xl font-bold text-sm flex items-center justify-center gap-2 shadow-md transition-all active:scale-98 disabled:opacity-60 cursor-pointer"
          >
            <Download className="w-4 h-4" />
            {downloading ? 'กำลังบันทึกภาพความคมชัดสูง...' : 'บันทึกรูปภาพสลิปใบเสร็จ (JPG HD)'}
          </button>

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleCopySummary}
              className="py-2.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copied ? 'คัดลอกสำเร็จ!' : 'คัดลอกข้อความสรุป'}</span>
            </button>
            <a
              href="https://lin.ee/PSM7qHqg"
              target="_blank"
              rel="noopener noreferrer"
              className="py-2.5 px-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-colors text-center"
            >
              <Share2 className="w-3.5 h-3.5" />
              <span>แจ้งทาง LINE OA (@997wvsow)</span>
            </a>
          </div>

          <button
            onClick={onClose}
            className="w-full text-center text-xs text-slate-500 hover:text-slate-800 py-1 font-medium transition-colors cursor-pointer"
          >
            ปิดหน้าต่าง / กลับสู่หน้าหลักเพื่อเลือกซื้อสินค้าต่อ
          </button>
        </div>
      </div>
    </div>
  );
};
